import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind } from "graphql";
import { getJourneyIdFromUrl } from "@volumental/journey-manager";
import { sendEvent } from "@volumental/link";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { createClient } from "graphql-ws";
import type React from "react";
import type { PropsWithChildren } from "react";
import { tokenStore } from "../auth/token-store";
import { GeneralErrorPage } from "../pages/error/GeneralErrorPage";
import { getApiURL, getWsApiURL } from "../util/select-api";

const clientId = new URLSearchParams(window.location.search).get("token");
const jid = getJourneyIdFromUrl();

const httpLink = new UploadHttpLink({
  uri: getApiURL(),
  headers: {
    "X-Visa-Jid": `${jid}`,
    "X-Visa-Token": `${clientId}`,
  },
});

const authLink = setContext((_, prevContext) => {
  const authToken = tokenStore.getToken();
  const prevHeaders = (prevContext as { headers?: Record<string, string> }).headers;
  return {
    headers: {
      ...prevHeaders,
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    },
  };
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: getWsApiURL(),
    keepAlive: 10_000,
    connectionParams: {
      "visa-token": `${clientId}`,
    },
  })
);

const link = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === Kind.OPERATION_DEFINITION && String(definition.operation) === "subscription";
  },
  wsLink,
  ApolloLink.from([authLink, httpLink])
);

const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      WidgetConfiguration: {
        keyFields: [],
        fields: {
          scanner: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming) as unknown;
            },
          },
          features: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming) as unknown;
            },
          },
        },
      },
      ScannerConfiguration: {
        keyFields: false,
      },
      FeatureConfiguration: {
        keyFields: false,
        fields: {
          recommendation: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming) as unknown;
            },
          },
          measurement: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming) as unknown;
            },
          },
        },
      },
      RecommendationFlowSettings: {
        keyFields: false,
      },
      MeasurementFlowSettings: {
        keyFields: false,
      },
    },
  }),
  link: ApolloLink.from([link]),
});

export function ApiProvider({ children }: PropsWithChildren): React.JSX.Element {
  const onClose = (): void => {
    sendEvent({
      type: "close",
      data: undefined,
    });
  };
  if (!clientId) {
    return <GeneralErrorPage errorText="Missing clientId" onClose={onClose} />;
  }
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
